import { NumberInput } from '@ksyos/design-system';
import { useEffect, useRef, useState } from 'react';
import { SearchableEnumDropdown } from '@frontend/components/elements/SearchableEnumDropdown';
import { getValidationErrorTranslation } from '@frontend/utils';

interface IInputPeriod {
    errors: string | undefined;
    fieldKey: string;
    handleChangeCustomControl: (values: { [key: string]: string | null | undefined }) => Promise<void>;
    validateOneCustomControl: (name: string) => Promise<void>;
    value: string | null;
}

enum SinglePeriodeTypeEnum {
    dagen = 'dag',
    jaar = 'jaar',
    maanden = 'maand',
    weken = 'week',
}

enum MultiplePeriodeTypeEnum {
    dagen = 'dagen',
    jaar = 'jaar',
    maanden = 'maanden',
    weken = 'weken',
}

export function InputPeriod(props: IInputPeriod) {
    const [period, setPeriod] = useState<string | null | undefined>('');
    const [hadValidPeriod, setHadValidPeriod] = useState(false);
    const [quantity, setQuantity] = useState<number | null>(null);
    const [unit, setUnit] = useState<MultiplePeriodeTypeEnum | null>(null);
    const root = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const matchRegex = /(\d+) (\w+)/;
        const matches = (props.value ?? '').match(matchRegex);

        if (matches) {
            setQuantity(parseInt(matches[1]));
            setUnit(normalizeUnit(matches[2]));
        }
    }, [props.value]);

    useEffect(() => {
        updatePeriod();
    }, [quantity, unit]);

    useEffect(() => {
        (async function () {
            let shouldValidate = hadValidPeriod;

            if (quantity && unit) {
                shouldValidate = true;
                setHadValidPeriod(true);
            }

            if (shouldValidate) {
                await props.handleChangeCustomControl({ [props.fieldKey]: period });
                props.validateOneCustomControl(props.fieldKey);
            }
        })();
    }, [period]);

    useEffect(() => {
        if (props.errors) {
            root.current?.closest('.has-danger')?.classList.add('multiple-input');
        }
    }, [props.errors]);

    const dropdownProps = {
        onChange: (value: SinglePeriodeTypeEnum | MultiplePeriodeTypeEnum | null) => {
            let newValue: MultiplePeriodeTypeEnum | null = null;

            if (quantity === 1) {
                const entryKey = Object.entries(SinglePeriodeTypeEnum).find((entry) => entry[1] === value);

                if (entryKey) {
                    newValue = MultiplePeriodeTypeEnum[entryKey[0] as keyof typeof MultiplePeriodeTypeEnum];
                }
            } else {
                newValue = value as MultiplePeriodeTypeEnum;
            }

            setUnit(newValue);
        },
        isSorted: true,
        error: unit ? '' : props.errors,
        automation: 'period-unit',
    };

    return (
        <div
            ref={root}
            style={{ display: 'flex', gap: 16 }}
        >
            <div style={{ flex: '0 1 160px' }}>
                <NumberInput
                    error={
                        quantity === 0
                            ? getValidationErrorTranslation('number_min')
                            : quantity && quantity > 99
                              ? getValidationErrorTranslation('number_max')
                              : quantity
                                ? ''
                                : props.errors
                                  ? getValidationErrorTranslation('any_only')
                                  : ''
                    }
                    label="Aantal..."
                    onChange={async (value) => {
                        setQuantity(value);
                    }}
                    value={quantity}
                />
            </div>
            <div
                className={unit ? 'is-correct' : ''}
                style={{ flex: 1 }}
            >
                {quantity === 1 && (
                    <SearchableEnumDropdown
                        {...dropdownProps}
                        jsEnum={SinglePeriodeTypeEnum}
                        placeholder={'Selecteer dag, week, maand of jaar'}
                        value={unit !== null ? SinglePeriodeTypeEnum[unit!] : null}
                    />
                )}

                {quantity !== 1 && (
                    <SearchableEnumDropdown
                        {...dropdownProps}
                        jsEnum={MultiplePeriodeTypeEnum}
                        placeholder={'Selecteer dagen, weken, maanden of jaar'}
                        value={unit}
                    />
                )}
            </div>
        </div>
    );

    async function updatePeriod() {
        let value: string | null = null;

        if (quantity && unit) {
            if (quantity === 1) {
                const entryKey = Object.entries(MultiplePeriodeTypeEnum).find((entry) => entry[1] === (unit as string));

                if (entryKey) {
                    value = `${quantity} ${SinglePeriodeTypeEnum[entryKey[1]]}`;
                }
            } else {
                value = `${quantity} ${unit}`;
            }
        }

        setPeriod(value);
    }

    function normalizeUnit(
        value: MultiplePeriodeTypeEnum | SinglePeriodeTypeEnum | string | null
    ): MultiplePeriodeTypeEnum | null {
        {
            const result = Object.values(MultiplePeriodeTypeEnum).find((entry) => entry === value);
            if (result) {
                return result;
            }
        }

        {
            const result = Object.entries(SinglePeriodeTypeEnum).find((entry) => entry[1] === value);
            if (result) {
                return result[0] as MultiplePeriodeTypeEnum;
            }
        }

        return null;
    }
}
